import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import BrowseLayout from '../../components/home/BrowseLayout';
import {
    LEGAL_ENTITY, LEGAL_STATE, LEGAL_EMAIL, EFFECTIVE_DATE,
    NOT_RECEIVED_DAYS, NOT_AS_DESCRIBED_DAYS, REFUND_BUSINESS_DAYS,
} from './legalInfo';
import './LegalPage.css';

const Mail = () => <a href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a>;

const SECTIONS = [
    ['about', 'About Gradies'],
    ['eligibility', 'Eligibility and Accounts'],
    ['listings', 'Listings, Grades and Pricing Data'],
    ['orders', 'Orders and Payment'],
    ['shipping', 'Shipping and Delivery'],
    ['refunds', 'No Returns; Refund Policy'],
    ['conduct', 'Acceptable Use'],
    ['ip', 'Intellectual Property'],
    ['third-party', 'Third-Party Services'],
    ['disclaimers', 'Disclaimers'],
    ['liability', 'Limitation of Liability'],
    ['indemnity', 'Indemnification'],
    ['disputes', 'Disputes and Governing Law'],
    ['changes', 'Changes and Termination'],
    ['general', 'General Terms'],
    ['contact', 'Contact'],
];

// User Agreement. Draft prepared without a lawyer; have counsel review before
// relying on it. Business details live in legalInfo.js.
const TermsPage = () => {
    // Open at the top, or at a section when linked with a hash (e.g. /terms#refunds).
    useEffect(() => {
        const target = window.location.hash && document.getElementById(window.location.hash.slice(1));
        if (target) target.scrollIntoView();
        else window.scrollTo(0, 0);
    }, []);

    return (
        <BrowseLayout>
            <article className="legal-doc">
                <h1>User Agreement</h1>
                <p className="legal-effective">Effective {EFFECTIVE_DATE}</p>

                <div className="legal-callout">
                    <p><strong>The short version.</strong> This summary is for convenience; the full terms below control.</p>
                    <ul>
                        <li>Gradies sells PSA graded Pokémon cards. Only brokers authorized by Gradies can list cards.</li>
                        <li>Every listing shows photos of the exact graded card and its PSA cert number, which you can check on PSA's site.</li>
                        <li>All sales are final and there are no returns. You get a refund if your card doesn't arrive or isn't as described.</li>
                        <li>PriceCharting prices are estimates for your information, not an appraisal or a promise of value.</li>
                        <li>New Jersey law governs these terms.</li>
                    </ul>
                </div>

                <p>
                    These terms are an agreement between you and {LEGAL_ENTITY}, a {LEGAL_STATE} limited liability
                    company ("Gradies," "we," "us"). They cover your use of gradies.com and any related pages or
                    services (the "Site"), including buying cards. By using the Site, creating an account or placing an
                    order, you agree to these terms and to our <Link to="/privacy">Privacy Policy</Link>. If you don't
                    agree, please don't use the Site.
                </p>

                <h2>Contents</h2>
                <ol className="legal-toc">
                    {SECTIONS.map(([id, title]) => (
                        <li key={id}><a href={`#${id}`}>{title}</a></li>
                    ))}
                </ol>

                <h2 id="about">1. About Gradies</h2>
                <p>
                    Gradies is an online store for trading cards that have been authenticated, graded and encapsulated
                    by Professional Sports Authenticator ("PSA"). Every card on the Site is listed by a broker that
                    Gradies has authorized. Members of the public cannot list or sell cards on the Site. When you buy
                    a card, your purchase is from Gradies, and Gradies is responsible to you for the order.
                </p>

                <h2 id="eligibility">2. Eligibility and Accounts</h2>
                <p>
                    You must be at least 18 years old, or the age of majority where you live, to buy on the Site.
                    You can browse without an account. To create an account you sign in with Google, and you're
                    responsible for keeping your Google account secure and for activity that happens under your
                    Gradies account. Tell us right away at <Mail /> if you think someone else has used your account.
                </p>
                <p>
                    We may suspend or close an account, or cancel orders, if we reasonably believe it's being used
                    for fraud, to break these terms or the law, or in a way that harms Gradies or other customers.
                </p>

                <h2 id="listings">3. Listings, Grades and Pricing Data</h2>
                <h3>Photos and cert numbers</h3>
                <p>
                    Each listing shows photos of the specific graded card being sold and its PSA certification number.
                    You can look up any cert number on PSA's website. The card you receive will carry the cert
                    number shown on the listing you bought.
                </p>
                <h3>Grades are PSA's</h3>
                <p>
                    The grade on each card is PSA's opinion of its condition. Gradies does not grade cards and does not
                    guarantee that PSA, another grading company, or anyone else would give the same grade today or in
                    the future. Disagreeing with PSA's grade does not make a card "not as described."
                </p>
                <h3>Pricing data is information, not advice</h3>
                <p>
                    We may show market data from PriceCharting and other sources, such as recent prices for the same
                    card at different PSA grades. This data comes from third parties, may be incomplete or out of
                    date, and is provided only to help you shop. It is not an appraisal, a guarantee of value, or
                    investment advice. Collectible values go up and down, and you buy based on your own judgment.
                </p>
                <h3>Errors</h3>
                <p>
                    We work to keep listings accurate, but mistakes happen. If a listing contains an error, such as a
                    wrong price, we may correct it and cancel any affected order. If we cancel an order you've paid
                    for, we'll refund you in full.
                </p>
                <h3>No affiliation</h3>
                <p>
                    Gradies is not affiliated with, endorsed by, or sponsored by PSA, PriceCharting, The Pokémon
                    Company, Nintendo, Creatures, Game Freak, or any card publisher. Their names and marks belong to
                    their owners and are used only to describe the cards we sell.
                </p>

                <h2 id="orders">4. Orders and Payment</h2>
                <p>
                    Prices are in U.S. dollars. The total you'll pay, including any shipping and any sales tax we're
                    required to collect, is shown before you confirm your order. Each listing is a single, unique
                    card, so it's available until someone buys it.
                </p>
                <p>
                    Payments are processed by PayPal, under PayPal's own terms. Gradies does not receive or store your
                    full card or bank account numbers. Your order is accepted when payment completes and we confirm
                    the order. We may decline or cancel an order before it ships if the card is no longer available,
                    the listing had an error, or we suspect fraud. If we cancel a paid order, we refund you in full.
                </p>

                <h2 id="shipping">5. Shipping and Delivery</h2>
                <p>
                    We ship to the address you provide at checkout, with tracking, and we'll send tracking details to
                    your email. Delivery estimates are estimates, not guarantees. Please make sure your address is
                    correct; we're not responsible for orders delivered to an address you entered incorrectly, though
                    we'll try to help. Responsibility for the card passes to you when the carrier delivers it to your
                    shipping address.
                </p>

                <h2 id="refunds">6. No Returns; Refund Policy</h2>
                <div className="legal-callout">
                    <p className="legal-caps">
                        All sales are final. We do not accept returns or exchanges, including for change of mind.
                        We will issue a refund if your card is not delivered or is not as described, as explained below.
                    </p>
                </div>
                <p>
                    Our <Link to="/guarantee">Buyer Trust Guarantee</Link> explains this policy in plain language.
                </p>
                <h3>Card not delivered</h3>
                <p>
                    If your card hasn't arrived, email <Mail /> within {NOT_RECEIVED_DAYS} days of your order date
                    with your order number. We'll look into it with the carrier. If the card was lost or not
                    delivered to your shipping address, we'll refund you in full.
                </p>
                <h3>Card not as described</h3>
                <p>
                    A card is "not as described" if what you receive is materially different from the listing: for
                    example, a different cert number, a different card, a different grade than the one on the listing,
                    or damage to the PSA case that isn't shown in the listing photos. Email <Mail /> within{' '}
                    {NOT_AS_DESCRIBED_DAYS} days of delivery with your order number and clear photos of the card, its
                    label and its case. Please keep the card and its packaging as they arrived.
                </p>
                <p>
                    If we confirm the card is not as described, we'll ask you to send it back using a prepaid
                    shipping label we provide, and we'll refund you in full, including any shipping you paid, once we
                    receive it. A difference of opinion with PSA's grade, or a change in market value, isn't a reason
                    for a refund.
                </p>
                <h3>How refunds are paid</h3>
                <p>
                    Approved refunds go back to your original payment method through PayPal within{' '}
                    {REFUND_BUSINESS_DAYS} business days of approval. Your bank or PayPal may take additional time to
                    show the credit. This policy doesn't limit any rights you have under PayPal's buyer protection
                    program or under laws that can't be waived.
                </p>

                <h2 id="conduct">7. Acceptable Use</h2>
                <p>When you use the Site, you agree not to:</p>
                <ul>
                    <li>break any law, or use the Site for fraud, including chargebacks for orders you received as described;</li>
                    <li>use bots, scrapers or other automated tools to access, copy or buy from the Site without our written permission;</li>
                    <li>interfere with the Site's security or operation, or try to access accounts, systems or data that aren't yours;</li>
                    <li>copy, resell or republish the Site's listings, photos or content except as these terms allow; or</li>
                    <li>impersonate anyone or give us false information.</li>
                </ul>

                <h2 id="ip">8. Intellectual Property</h2>
                <p>
                    The Site, including its design, text, graphics, photos we take and software, belongs to Gradies or
                    our licensors and is protected by law. You may use the Site for your own personal shopping. You
                    may not copy, modify, distribute or create works from it without our written permission. Card
                    artwork and game names belong to their respective owners.
                </p>

                <h2 id="third-party">9. Third-Party Services</h2>
                <p>
                    The Site relies on and links to services run by others, including Google (sign-in), PayPal
                    (payments), PSA (cert lookups) and PriceCharting (market data). Those services have their own terms
                    and privacy policies, and we're not responsible for their content or how they operate.
                </p>

                <h2 id="disclaimers">10. Disclaimers</h2>
                <p className="legal-caps">
                    Except as expressly stated in these terms, including our refund policy, the Site and all
                    information on it are provided "as is" and "as available," and to the fullest extent the law
                    allows, Gradies disclaims all other warranties, express or implied, including warranties of
                    merchantability, fitness for a particular purpose and non-infringement. We don't warrant that the
                    Site will be uninterrupted or error-free, or that third-party data such as pricing is accurate.
                </p>

                <h2 id="liability">11. Limitation of Liability</h2>
                <p className="legal-caps">
                    To the fullest extent the law allows, Gradies and its members, managers, employees, brokers and
                    agents will not be liable for any indirect, incidental, special, consequential or punitive
                    damages, or for lost profits or lost value of any card, arising from your use of the Site or any
                    purchase. Our total liability for any claim relating to an order is limited to the amount you
                    paid for that order, and for any other claim to one hundred U.S. dollars ($100).
                </p>
                <p>
                    Some laws don't allow these limits, so they may not fully apply to you. Nothing in these terms
                    limits liability that can't be limited by law, or rights you have under New Jersey consumer
                    protection law that can't be waived.
                </p>

                <h2 id="indemnity">12. Indemnification</h2>
                <p>
                    If you break these terms or the law, you agree to cover the reasonable losses, costs and legal fees
                    Gradies incurs as a result, to the extent the law allows.
                </p>

                <h2 id="disputes">13. Disputes and Governing Law</h2>
                <p>
                    If you have a problem, please contact us first at <Mail />. Most issues can be resolved quickly
                    that way, and both of us agree to try in good faith for 30 days before starting a formal claim.
                </p>
                <p>
                    These terms are governed by the laws of the State of {LEGAL_STATE}, without regard to its
                    conflict-of-law rules. Any lawsuit relating to these terms or the Site will be brought in the
                    state or federal courts located in {LEGAL_STATE}, and you and Gradies consent to those courts'
                    jurisdiction. Either of us may bring an individual claim in small claims court instead if it
                    qualifies.
                </p>

                <h2 id="changes">14. Changes and Termination</h2>
                <p>
                    We may update these terms from time to time. We'll post the new version on this page with a new
                    effective date, and for significant changes we'll take reasonable steps to let you know, such as
                    a notice on the Site. Changes apply going forward and don't affect orders placed before them. If
                    you keep using the Site after changes take effect, you accept the updated terms.
                </p>
                <p>
                    You can stop using the Site at any time. Sections that by their nature should survive, such as
                    the refund policy for orders already placed, disclaimers, limits of liability and governing law,
                    continue to apply.
                </p>

                <h2 id="general">15. General Terms</h2>
                <p>
                    These terms and our Privacy Policy are the entire agreement between you and Gradies about the
                    Site. If any part of these terms is found unenforceable, the rest stays in effect. Our not
                    enforcing a provision isn't a waiver of it. You can't transfer your rights under these terms
                    without our consent; we may transfer ours in connection with a merger, sale or reorganization.
                    Neither of us is responsible for delays caused by events beyond our reasonable control.
                </p>

                <h2 id="contact">16. Contact</h2>
                <p>
                    {LEGAL_ENTITY}<br />
                    {LEGAL_STATE}<br />
                    Email: <Mail />
                </p>
            </article>
        </BrowseLayout>
    );
};

export default TermsPage;
